import React, { useState, useRef, useEffect } from 'react';
import { mockData } from '../data/mock';
import { Filter, MapPin, Calendar, ChevronDown } from 'lucide-react';

const Projects = () => {
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const extraGridRef = useRef(null);
  const [extraHeight, setExtraHeight] = useState(0);

  const extraProjects = mockData.allProjects || [];

  // Compute all projects for filtering
  const allProjectsList = [...mockData.projects, ...extraProjects];

  // Filtrer les projets par agence
  const filteredMain = selectedAgency === 'all' 
    ? mockData.projects 
    : mockData.projects.filter(project => project.agency === selectedAgency);

  const filteredExtra = selectedAgency === 'all'
    ? extraProjects
    : extraProjects.filter(project => project.agency === selectedAgency);

  // Measure the extra grid height for smooth animation
  useEffect(() => {
    if (extraGridRef.current) {
      setExtraHeight(extraGridRef.current.scrollHeight);
    }
  }, [showAll, filteredExtra, selectedAgency]);


  const handleToggle = () => {
    setShowAll(prev => !prev);
  };

  const renderProjectCard = (project, index, delayBase = 0) => (
    <div 
      key={project.id} 
      className="project-card"
      data-aos="fade-up"
      data-aos-delay={(delayBase + index) * 100}
    >
      <div className="project-image-wrapper">
        <img 
          src={project.image} 
          alt={project.alt || ''}
          className="project-image"
        />
        <div className="project-overlay">
          <div className="project-tags">
            {project.tags.map((tag, tagIndex) => (
              <span key={tagIndex} className="project-tag">{tag}</span>
            ))}
          </div>
        </div>
        {/* Badge agence */}
        <div className="absolute top-4 left-4 bg-[#1E3A33] text-[#8BC34A] px-3 py-1 rounded-full text-sm font-semibold backdrop-blur-sm flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {project.agency}
        </div>
      </div>
      <div className="project-content">
        <h3 className="project-title">{project.title}</h3>
        <p className="project-description">{project.description}</p>
        
        {/* Infos supplémentaires */}
        <div className="mt-3 flex items-center gap-4 text-sm text-[#B8C4B0]">
          {project.surface && (
            <span className="font-semibold">{project.surface}</span>
          )}
          {project.year && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {project.year}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section id="realisations" className="section-projects">
      <div className="container">
        <div 
          className="section-header"
          data-aos="fade-up"
        >
          <h2 className="section-title">Nos réalisations</h2>
          <p className="section-subtitle">
            Découvrez nos réalisations de maisons individuelles à travers le sud de la France
          </p>
        </div>

        {/* Filtres par agence */}
        <div 
          className="mb-8 flex flex-wrap gap-3 justify-center items-center"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <span className="text-[#B8C4B0] font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtrer par secteur :
          </span>
          <button
            onClick={() => setSelectedAgency('all')}
            className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
              selectedAgency === 'all'
                ? 'bg-[#70AD25] text-[#1E3A33] shadow-lg'
                : 'bg-[#2A4A42] text-white hover:bg-[#3A5A4A]'
            }`}
          >
            Tous ({allProjectsList.length})
          </button>
          {mockData.agencies.map((agency) => {
            const count = allProjectsList.filter(p => p.agency === agency.name).length;
            return (
              <button
                key={agency.id}
                onClick={() => setSelectedAgency(agency.name)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  selectedAgency === agency.name
                    ? 'bg-[#70AD25] text-[#1E3A33] shadow-lg'
                    : 'bg-[#2A4A42] text-white hover:bg-[#3A5A4A]'
                }`}
              >
                {agency.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Main projects grid */}
        <div className="projects-grid">
          {filteredMain.map((project, index) => renderProjectCard(project, index))}
        </div>

        {/* Extra projects - expandable */}
        {filteredExtra.length > 0 && (
          <div 
            className="projects-extra-wrapper"
            style={{
              maxHeight: showAll ? extraHeight + 'px' : '0px',
              opacity: showAll ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease'
            }}
          >
            <div ref={extraGridRef} className="projects-grid projects-grid-extra">
              {filteredExtra.map((project, index) => renderProjectCard(project, index, filteredMain.length))}
            </div>
          </div>
        )}

        {(filteredMain.length === 0 && filteredExtra.length === 0) && (
          <div className="text-center py-16">
            <p className="text-[#B8C4B0] text-lg">
              Aucune réalisation dans ce secteur pour le moment.
            </p>
          </div>
        )}

        {/* Toggle button */}
        {filteredExtra.length > 0 && (
          <div className="projects-cta-wrapper" data-aos="fade-up" data-aos-delay="200">
            <button 
              onClick={handleToggle} 
              className={`projects-cta-link projects-cta-toggle ${showAll ? 'is-expanded' : ''}`}
            >
              <span>{showAll ? 'Voir moins' : 'Voir toutes nos réalisations'}</span>
              <ChevronDown size={20} className={`projects-cta-chevron ${showAll ? 'rotated' : ''}`} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
